import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Eye, EyeOff, Lock, AlertTriangle, Shield } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const AdminLoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { signIn, loading: isLoading } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await signIn(formData.email, formData.password);
      navigate('/admin/dashboard');
    } catch {
      setError('Ungültige Anmeldedaten. Bitte überprüfe E-Mail und Passwort.');
    }
  };

  return (
    <div className="min-h-screen flex">
      <SEO title="Admin Login" description="Admin-Bereich für Arabicroots" url="/admin/login" noIndex />

      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[40%] bg-[#970000] flex-col justify-between p-12 xl:p-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px'}} />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-black/20 rounded-full blur-3xl" />

        <div className="relative">
          <Link to="/">
            <img src="/LogoHeader4.png" alt="Arabicroots" className="h-12 w-auto object-contain brightness-0 invert mb-14" onError={e => (e.currentTarget.style.display = 'none')} />
          </Link>
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
            <Lock className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-3xl xl:text-4xl font-bold text-white mb-4 leading-tight">
            Admin-Bereich
          </h1>
          <p className="text-white/70 text-base leading-relaxed max-w-xs">
            Zugang zum internen Verwaltungsbereich von Arabicroots.
          </p>
        </div>

        <div className="relative flex items-center gap-3 bg-black/15 border border-white/15 rounded-2xl px-5 py-4">
          <Shield className="h-5 w-5 text-white/70 shrink-0" />
          <p className="text-white/70 text-sm">Gesicherter interner Zugang</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 bg-gray-50 flex flex-col">
        <div className="lg:hidden px-5 pt-5">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
            ← Zur Website
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-sm">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-8">
              <img src="/LogoHeader4.png" alt="Arabicroots" className="h-14 w-auto object-contain mx-auto" onError={e => (e.currentTarget.style.display = 'none')} />
            </div>

            <div className="mb-8">
              <p className="text-xs font-bold tracking-widest uppercase text-[#970000] mb-2">Sicherer Zugang</p>
              <h2 className="text-2xl font-bold text-gray-900">Willkommen zurück</h2>
              <p className="text-gray-500 text-sm mt-1">Bitte gib deine Zugangsdaten ein.</p>
            </div>

            {error && (
              <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">E-Mail-Adresse</label>
                  <input
                    type="email" id="email" name="email"
                    value={formData.email} onChange={handleChange}
                    placeholder="admin@arabicroots.de" required disabled={isLoading}
                    className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3.5 text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:border-[#970000]/50 focus:ring-2 focus:ring-[#970000]/10 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">Passwort</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'} id="password" name="password"
                      value={formData.password} onChange={handleChange}
                      placeholder="••••••••" required disabled={isLoading}
                      className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3.5 pr-10 text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:border-[#970000]/50 focus:ring-2 focus:ring-[#970000]/10 text-sm"
                    />
                    <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit" disabled={isLoading}
                  className="w-full h-11 rounded-xl bg-[#970000] text-white font-semibold hover:bg-[#7a0000] transition-colors disabled:bg-gray-200 disabled:text-white/70 disabled:cursor-not-allowed text-sm"
                >
                  {isLoading ? 'Wird angemeldet…' : 'Einloggen'}
                </button>
              </form>
            </div>

            <div className="mt-5 text-center">
              <Link to="/" className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                Zurück zur Startseite
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
